import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import leftQuote from "../images/MediaPage_LeftQuote.png";
import RelatedPosts from "../components/RelatedPosts";
import "./BlogPost.css";

const API_URL = process.env.REACT_APP_API_URL;

function BlogPost() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        const controller = new AbortController();

        setLoading(true);
        setError("");
        setBlog(null);

        fetch(`${API_URL}/api/blogs/${slug}`, { signal: controller.signal })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(response.status === 404
                        ? "This story could not be found."
                        : "This story could not be loaded.");
                }
                return response.json();
            })
            .then((data) => {
                if (!data || typeof data !== "object") {
                    throw new Error("This story could not be found.");
                }
                setBlog(data);
            })
            .catch((requestError) => {
                if (requestError.name !== "AbortError") {
                    setError(requestError.message || "This story could not be loaded.");
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [slug]);

    const processedContent = useMemo(() => {
        if (!blog?.content) return "";

        return blog.content.replace(
            /<special>([\s\S]*?)<\/special>/gi,
            '<div class="blog-special-quote"><div class="blog-special-quote-text">$1</div></div>'
        );
    }, [blog]);

    if (loading) {
        return <main className="blog-post-state" aria-live="polite">Loading story...</main>;
    }

    if (error || !blog) {
        return (
            <main className="blog-post-state blog-post-error" role="alert">
                <h1>Sorry, we couldn't open this story.</h1>
                <p>{error || "This story could not be found."}</p>
            </main>
        );
    }

    return (
        <main className="blog-post-page">
            <article className="blog-post">
                <header className="blog-post-header">
                    <div className="blog-post-categories">
                        {(blog.categories ?? []).map((category) => (
                            <span key={category}>{category}</span>
                        ))}
                    </div>

                    <p className="blog-post-meta">
                        <span>{blog.author}</span>
                        {blog.author && blog.date && <span aria-hidden="true">|</span>}
                        <time>{blog.date}</time>
                    </p>

                    <h1>{blog.title}</h1>

                    <div className="blog-post-image-wrap">
                        <img
                            src={require(`../images/${blog.image}`)}
                            alt={blog.title}
                            className="blog-post-image"
                        />
                    </div>
                </header>

                <div
                    className="blog-post-content"
                    style={{ "--quote-image": `url(${leftQuote})` }}
                    dangerouslySetInnerHTML={{ __html: processedContent }}
                />
            </article>

            <RelatedPosts currentSlug={slug} />
        </main>
    );
}

export default BlogPost;
